import { ComponentType } from 'react';

import { BillToAddressCard, CompanyCard, SoldToAddressCard } from '../../../../../components/cards/company';
import { FinancialAttachmentCard, PayerAddressCard } from '../../../../../components/cards/financial';
import { AdditionalAttachmentCard } from '../../../../../components/cards/financial/AttachmentCard';
import PrivacyPolicyCard from '../../../../../components/cards/financial/PrivacyPolicyCard';
import { ShipToAddressCard } from '../../../../../components/cards/shipTo';
import CompanyPage from '../../../../../features/CompanyPage';
import FinancialPage from '../../../../../features/FinancialPage';
import ReviewPage from '../../../../../features/ReviewPage';
import ShipToPage from '../../../../../features/ShipTo';
import { CARD_CATEGORIES } from '../../../../../shared/cardConfig/constants';
import {
  ATTACHMENT_FIELDS,
  BILL_TO_ADDRESS_FIELDS,
  COMPANY_FIELDS,
  PAYER_ADDRESS_FIELDS,
  SHIP_TO_ADDRESS_FIELDS,
  SOLD_TO_ADDRESS_FIELDS
} from '../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../utils/constants';
import { getShipToSubtitle } from '../../../../../utils/hooksLib';
import billToPages from './billTo';
import payerPages from './payer';
import shipToPages from './shipTo';
import soldToPages from './soldTo';

// ----------------- Type Definitions -----------------
interface Field {
  key: string;
  required?: boolean;
}

interface Card {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  fields?: Field[];
  hideSameAs?: boolean;
  isCardOptional?: boolean;
  fieldWrapper?: string;
  errorMustContain?: string;
}

interface Page {
  route: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  cards?: Card[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accordions?: Array<{ title: string; cards: Card[] } | ((args: any) => any)>;
  validateFinancials?: boolean;
}

// ----------------- Page Templates -----------------
const companyPageTemplate: Page = {
  route: PAGE_ROUTES.company,
  Component: CompanyPage,
  cards: [
    {
      key: CARD_CATEGORIES.company,
      Component: CompanyCard,
      fields: [
        { key: COMPANY_FIELDS.legalName },
        { key: COMPANY_FIELDS.tradeDbaName, required: true }
      ]
    },
    {
      key: CARD_CATEGORIES.soldTo,
      Component: SoldToAddressCard,
      fields: [
        { key: SOLD_TO_ADDRESS_FIELDS.address },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber1, required: true },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: SOLD_TO_ADDRESS_FIELDS.email },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone1 },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone2 }
      ]
    },
    {
      key: CARD_CATEGORIES.billTo,
      Component: BillToAddressCard,
      isCardOptional: true,
      fields: [
        { key: BILL_TO_ADDRESS_FIELDS.address },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber1 },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber2 }
      ]
    }
  ]
};

const shipToPageTemplate: Page = {
  route: PAGE_ROUTES.shipTo,
  Component: ShipToPage,
  cards: [
    {
      key: CARD_CATEGORIES.address,
      Component: ShipToAddressCard,
      fields: [
        { key: SHIP_TO_ADDRESS_FIELDS.address },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber1 },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: SHIP_TO_ADDRESS_FIELDS.email },
        { key: SHIP_TO_ADDRESS_FIELDS.telephone1 },
        { key: SHIP_TO_ADDRESS_FIELDS.telephone2 }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    }
  ]
};

const financialPageTemplate: Page = {
  route: PAGE_ROUTES.financial,
  Component: FinancialPage,
  validateFinancials: true,
  cards: [
    {
      key: CARD_CATEGORIES.payer,
      Component: PayerAddressCard,
      fields: [
        { key: PAYER_ADDRESS_FIELDS.address },
        { key: PAYER_ADDRESS_FIELDS.taxNumber1, required: true },
        { key: PAYER_ADDRESS_FIELDS.taxNumber2 },
        { key: PAYER_ADDRESS_FIELDS.telephone1 },
        { key: PAYER_ADDRESS_FIELDS.telephone2 }
      ]
    },
    {
      key: CARD_CATEGORIES.financialAttachmentCard,
      Component: FinancialAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.financialCardAttachment }]
    },
    {
      key: CARD_CATEGORIES.additionalAttachmentCard,
      Component: AdditionalAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.additionalAttachment }]
    },
    {
      key: CARD_CATEGORIES.privacyPolicy,
      Component: PrivacyPolicyCard
    }
  ]
};

// ----------------- Dynamic ShipTo Cards for Review Page -----------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getShipToCardsForReviewPage({ store }: { store: any }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return store.customer.shipTo.map((shipTo: any, i: number) => ({
    title: `tabTitles.ship to`,
    count: i + 1,
    subtitle: getShipToSubtitle({ shipToAddress: shipTo.address, store }),
    cards:
      i === 0
        ? shipToPageTemplate.cards
        : shipToPageTemplate.cards!.filter(({ key }) => CARD_CATEGORIES.invoiceDelivery !== key)
  }));
}

const reviewPageTemplate: Page = {
  route: PAGE_ROUTES.review,
  Component: ReviewPage,
  accordions: [
    {
      title: 'tabTitles.company',
      cards: companyPageTemplate.cards!
    },
    getShipToCardsForReviewPage,
    {
      title: 'tabTitles.financial',
      cards: financialPageTemplate.cards!
    }
  ]
};

// ----------------- Template Map -----------------
export const templateMap: Record<string, { pages: Page[] }> = {
  customer_set_up: {
    pages: [companyPageTemplate, shipToPageTemplate, financialPageTemplate, reviewPageTemplate]
  },
  bill_to: {
    pages: billToPages
  },
  payer: {
    pages: payerPages
  },
  ship_to: {
    pages: shipToPages
  },
  sold_to: {
    pages: soldToPages
  }
};